/**
 * BlockLifecycleManager — manages block registration, init flow execution,
 * and generation-based StrictMode safety.
 *
 * Replaces the old pattern of re-creating the entire runtime when blocks change.
 * Instead, blocks are added incrementally and existing state is preserved.
 */

import type { BlockDef, Flow } from "@cogo/types-v2"

// ─── Types ───────────────────────────────────────────────────────────────────

export interface BlockInitTracker {
  /** Whether this block's init flows have been executed for the current page */
  initDone: boolean
  /** The page ID for which init was last run */
  pageId: string | null
}

export interface LifecycleCallbacks {
  /** Initialize state for a single block (state.json defaults, datasources, computed) */
  initBlockState(blockName: string, block: BlockDef): void
  /** Register flows from a single block */
  registerBlockFlows(blockName: string, block: BlockDef): void
  /** Register event bindings from a single block */
  registerBlockBindings(blockName: string, block: BlockDef): void
  /** Execute a single flow and return promise */
  executeFlow(flow: Flow, context: Record<string, unknown>): Promise<void>
  /** Find a flow by its full name */
  findFlow(name: string): Flow | undefined
  /** Get all registered flow names */
  getRegisteredFlowNames(): string[]
}

// ─── Constants ───────────────────────────────────────────────────────────────

/** Default priority for content block init flows */
const CONTENT_PRIORITY = 100
/** Priority for layout block init flows (run first) */
const LAYOUT_PRIORITY = 10

/**
 * Flow names a block declares to auto-run on MOUNT, across both lifecycle shapes the generators emit:
 *   - corpus  (block.cogo.json → BlockDef.meta): meta.lifecycle.onMount = "flowName" (string)
 *   - agentic (events stage):                    events.lifecycle.{onMount,onBlockLoad} = { flow: "flowName" }
 * The referenced flow carries trigger:"lifecycle"/"mount" (not "init"), so activatePage's init-only gate
 * would otherwise never run it — the verified seam (docs/agentic/ontology/lifecycle-init-execution-e2e).
 * Deliberately EXCLUDES onUnmount / onPropsChange: those are not mount-time and have no runtime path yet.
 */
function extractMountFlowNames(block: BlockDef): string[] {
  const out: string[] = []
  const sources: unknown[] = [
    // BlockMeta has no index signature (TS2352 bridge) — block.cogo.json's lifecycle lands on meta.
    (block.meta as unknown as Record<string, unknown> | undefined)?.lifecycle,
    (block.events as unknown as Record<string, unknown> | undefined)?.lifecycle,
  ]
  for (const lc of sources) {
    if (!lc || typeof lc !== "object") continue
    for (const key of ["onMount", "onBlockLoad"]) {
      const v = (lc as Record<string, unknown>)[key]
      if (typeof v === "string" && v) out.push(v)
      else if (v && typeof v === "object" && typeof (v as { flow?: unknown }).flow === "string") {
        out.push((v as { flow: string }).flow)
      }
    }
  }
  return out
}

// ─── BlockLifecycleManager ──────────────────────────────────────────────────

export class BlockLifecycleManager {
  private readonly blocks = new Map<string, BlockDef>()
  private readonly initTrackers = new Map<string, BlockInitTracker>()
  private generation = 0
  private readonly callbacks: LifecycleCallbacks

  constructor(callbacks: LifecycleCallbacks) {
    this.callbacks = callbacks
  }

  // ─── Public API ──────────────────────────────────────────────────────────

  /**
   * Add new blocks incrementally. Skips blocks that are already registered.
   * For each new block: initializes state, registers flows, registers bindings.
   * Returns the list of newly added block names.
   */
  addBlocks(blocks: Record<string, BlockDef>): string[] {
    const added: string[] = []
    for (const [name, block] of Object.entries(blocks)) {
      const existing = this.blocks.get(name)
      // Replace placeholder blocks (meta.type === 'placeholder') with real blocks
      if (existing) {
        const isPlaceholder = (existing.meta as unknown as Record<string, unknown>)?.type === 'placeholder' // TS2352 bridge — BlockMeta has no index signature; reading placeholder discriminant only
        if (!isPlaceholder) continue  // skip already-registered real blocks
        // Fall through to replace placeholder with real block
      }
      this.blocks.set(name, block)
      this.initTrackers.set(name, { initDone: false, pageId: null })
      this.callbacks.initBlockState(name, block)
      this.callbacks.registerBlockFlows(name, block)
      this.callbacks.registerBlockBindings(name, block)
      added.push(name)
    }
    return added
  }

  /**
   * Get all registered blocks.
   */
  getAllBlocks(): Record<string, BlockDef> {
    const result: Record<string, BlockDef> = {}
    for (const [name, block] of this.blocks) {
      result[name] = block
    }
    return result
  }

  /**
   * Get a single block by name.
   */
  getBlock(name: string): BlockDef | undefined {
    return this.blocks.get(name)
  }

  /**
   * Check if a block is registered.
   */
  hasBlock(name: string): boolean {
    return this.blocks.has(name)
  }

  /**
   * Number of registered blocks.
   */
  get size(): number {
    return this.blocks.size
  }

  /**
   * Activate a page: execute init flows in 2 phases.
   * Phase 1: Layout flows (priority < 50) — awaited sequentially.
   * Phase 2: Content flows — each block's steps run sequentially, blocks run in parallel.
   *
   * @param activeBlockNames - block names active on this page
   * @param pageId - current page ID
   * @param context - flow execution context (route params, etc.)
   */
  async activatePage(
    activeBlockNames: Set<string>,
    pageId: string,
    context: Record<string, unknown>,
  ): Promise<void> {
    const gen = this.generation

    // Collect init flows grouped by priority
    const layoutFlows: Array<{ name: string; flow: Flow }> = []
    const contentFlows: Array<{ name: string; flow: Flow }> = []
    const activeNamespaces = new Set<string>()

    for (const blockName of activeBlockNames) {
      const block = this.blocks.get(blockName)
        ?? this.blocks.get(camelToKebab(blockName))
        ?? this.blocks.get(kebabToCamel(blockName))
      const declaredNs = (block?.meta as { namespace?: string } | undefined)?.namespace
      for (const variant of [blockName, declaredNs]) {
        if (!variant) continue
        activeNamespaces.add(variant)
        activeNamespaces.add(camelToKebab(variant))
        activeNamespaces.add(kebabToCamel(variant))
      }
    }

    // Mount lifecycle hooks: a flow named by block.lifecycle.onMount (corpus) or
    // events.lifecycle.onBlockLoad/onMount (agentic) must run on mount even though its trigger is
    // "lifecycle"/"mount" rather than "init". Collect those bare flow names per active block, keyed by
    // every case variant of the block name and its declared namespace.
    const mountNamesByNs = new Map<string, Set<string>>()
    for (const blockName of activeBlockNames) {
      const block = this.blocks.get(blockName)
        ?? this.blocks.get(camelToKebab(blockName))
        ?? this.blocks.get(kebabToCamel(blockName))
      if (!block) continue
      const names = extractMountFlowNames(block)
      if (names.length === 0) continue
      const set = new Set(names)
      const declaredNs = (block.meta as { namespace?: string } | undefined)?.namespace
      for (const variant of [blockName, declaredNs]) {
        if (!variant) continue
        mountNamesByNs.set(variant, set)
        mountNamesByNs.set(camelToKebab(variant), set)
        mountNamesByNs.set(kebabToCamel(variant), set)
      }
    }

    const allFlowNames = this.callbacks.getRegisteredFlowNames()
    for (const flowName of allFlowNames) {
      const flow = this.callbacks.findFlow(flowName)
      if (!flow) continue

      // Check if flow belongs to an active block
      const ns = flowName.split(".")[0]
      const nsKebab = camelToKebab(ns)
      const nsCamel = kebabToCamel(ns)
      if (activeBlockNames.size > 0 && !activeNamespaces.has(ns) && !activeNamespaces.has(nsKebab) && !activeNamespaces.has(nsCamel)) continue

      // Run on mount when trigger === "init" OR the flow is referenced by a mount lifecycle hook
      // (block.lifecycle.onMount / events.lifecycle.onBlockLoad). onUnmount/onPropsChange are excluded.
      const mountSet = mountNamesByNs.get(ns) ?? mountNamesByNs.get(nsKebab) ?? mountNamesByNs.get(nsCamel)
      const isMountHook = mountSet?.has(flowName.slice(ns.length + 1)) ?? false
      if (flow.trigger !== "init" && !isMountHook) continue

      // Check if already executed for this page (unless resetForRoute was called)
      // Tracker may be stored under any case variant, so try all
      const tracker = this.findInitTracker(ns)
      if (tracker?.initDone && tracker.pageId === pageId) continue

      const priority = flow.priority ?? CONTENT_PRIORITY
      if (priority < 50) {
        layoutFlows.push({ name: flowName, flow })
      } else {
        contentFlows.push({ name: flowName, flow })
      }
    }

    // Phase 1: Layout flows — sequential, awaited
    for (const { name, flow } of layoutFlows) {
      if (this.generation !== gen) return // stale — abort
      try {
        await this.callbacks.executeFlow(flow, context)
        this.markInitDone(name, pageId)
      } catch (err) {
        console.warn(`[BlockLifecycle] Layout init flow error (${name}):`, err)
      }
    }

    // Phase 2: Content flows — blocks in parallel, steps sequential within each flow
    if (this.generation !== gen) return // stale — abort
    const contentPromises = contentFlows.map(async ({ name, flow }) => {
      if (this.generation !== gen) return
      try {
        await this.callbacks.executeFlow(flow, context)
        this.markInitDone(name, pageId)
      } catch (err) {
        console.warn(`[BlockLifecycle] Content init flow error (${name}):`, err)
      }
    })
    await Promise.all(contentPromises)
  }

  /**
   * Reset init tracking for a route change, allowing init flows to re-run.
   */
  resetForRoute(pageId?: string): void {
    for (const tracker of this.initTrackers.values()) {
      if (!pageId || tracker.pageId !== pageId) {
        tracker.initDone = false
        tracker.pageId = null
      }
    }
  }

  /**
   * Increment generation counter. Used for StrictMode safety:
   * - Call on mount to get current generation
   * - Call on unmount to invalidate stale async results
   * Returns the new generation number.
   */
  nextGeneration(): number {
    return ++this.generation
  }

  /**
   * Get current generation (for external checks).
   */
  getGeneration(): number {
    return this.generation
  }

  /**
   * Clean up all state.
   */
  destroy(): void {
    this.blocks.clear()
    this.initTrackers.clear()
    this.generation = Number.MAX_SAFE_INTEGER // invalidate all pending
  }

  // ─── Private ───────────────────────────────────────────────────────────

  private markInitDone(flowName: string, pageId: string): void {
    const ns = flowName.split(".")[0]
    const tracker = this.findInitTracker(ns)
    if (tracker) {
      tracker.initDone = true
      tracker.pageId = pageId
    }
  }

  private findInitTracker(identifier: string): BlockInitTracker | undefined {
    const direct = this.initTrackers.get(identifier)
      ?? this.initTrackers.get(camelToKebab(identifier))
      ?? this.initTrackers.get(kebabToCamel(identifier))
    if (direct) return direct

    const variants = new Set([identifier, camelToKebab(identifier), kebabToCamel(identifier)])
    for (const [blockName, block] of this.blocks) {
      const declaredNs = (block.meta as { namespace?: string } | undefined)?.namespace
      if (!declaredNs) continue
      if (
        variants.has(declaredNs) ||
        variants.has(camelToKebab(declaredNs)) ||
        variants.has(kebabToCamel(declaredNs))
      ) {
        return this.initTrackers.get(blockName)
      }
    }
    return undefined
  }
}

// ─── Utility ────────────────────────────────────────────────────────────────

const camelToKebab = (s: string) => s.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase()
const kebabToCamel = (s: string) => s.replace(/-([a-z])/g, (_, c) => c.toUpperCase())
